import { useCallback, useEffect, useState } from 'react';
import FriendsServiceDB from '../services/friendsServiceDB';
import { getLocalDateString } from '../utils/dateUtils';
import { tripLength } from '../utils/homePlans';
import { withoutBlockedUsers } from '../utils/blockedCards';

/**
 * "While you're in <place>": things to do at a planned trip's destination —
 * events on during the trip's dates, and places, tours and walks near where
 * you'll sleep (the stay) or the destination. Two feed requests fenced to
 * the trip's own centre, interleaved so neither kind buries the other.
 *
 * `entry` is one of buildPlans' `away` entries: { trip, stage, center }.
 * The events request is skipped for an undated idea (no window to fence to)
 * and for a trip whose dates are over.
 *
 * Cached per trip and centre for five minutes: the chips switch trips often,
 * and a trip's destination content doesn't change by the minute.
 */
const cache = new Map();
const TTL_MS = 5 * 60 * 1000;
const EVENT_LIMIT = 8;
const PLACE_LIMIT = 8;
const UNDATED_TYPES = 'places,tours';

// Mirrors the feed's radiusForStayLength: a weekend wants the city, a fortnight
// the region. A stay narrows it: "near your stay" means walking and transit.
export function awayRadiusKm(trip, center) {
  if (center?.kind === 'stay') return 25;
  const days = tripLength(trip);
  if (!days) return 50;
  if (days <= 1) return 25;
  if (days <= 3) return 50;
  if (days <= 7) return 75;
  return 100;
}

export function interleave(events, places) {
  const out = [];
  const a = [...events];
  const b = [...places];
  while (a.length || b.length) {
    if (a.length) out.push(a.shift());
    if (a.length) out.push(a.shift());
    if (b.length) out.push(b.shift());
    if (b.length) out.push(b.shift());
  }
  return out;
}

const cacheKey = (entry) => `${entry.trip.id}:${entry.center.lat.toFixed(3)},${entry.center.lng.toFixed(3)}`;

export const invalidateAwayIdeas = (tripId) => {
  for (const key of cache.keys()) {
    if (!tripId || key.startsWith(`${tripId}:`)) cache.delete(key);
  }
};

async function fetchIdeas(entry, today) {
  const { trip, center } = entry;
  const radiusKm = awayRadiusKm(trip, center);
  const start = (trip.startDate || trip.start_date || '').slice(0, 10);
  const end = (trip.endDate || trip.end_date || start || '').slice(0, 10);
  const dated = !!start && end >= today;
  const base = { sort: 'ranked', lat: center.lat, lng: center.lng, radiusKm };
  const [events, places] = await Promise.all([
    dated
      ? FriendsServiceDB.getFeed({ ...base, contentType: 'events', limit: EVENT_LIMIT,
        dateStart: start > today ? start : today, dateEnd: end })
      : Promise.resolve({ feed: [] }),
    FriendsServiceDB.getFeed({ ...base, contentType: 'selected', contentTypes: UNDATED_TYPES, limit: PLACE_LIMIT })
  ]);
  if (events.error && places.error) throw new Error(typeof places.error === 'string' ? places.error : 'feed');
  return withoutBlockedUsers(interleave(events.feed || [], places.feed || []));
}

export default function useAwayIdeas(entry, today = getLocalDateString(new Date())) {
  const key = entry?.center ? cacheKey(entry) : null;
  const [state, setState] = useState({ items: [], loading: !!key, error: null });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!key) { setState({ items: [], loading: false, error: null }); return undefined; }
    const hit = cache.get(key);
    if (hit && Date.now() - hit.ts < TTL_MS) {
      setState({ items: hit.items, loading: false, error: null });
      return undefined;
    }
    let alive = true;
    setState(prev => ({ ...prev, loading: true, error: null }));
    fetchIdeas(entry, today)
      .then(items => {
        cache.set(key, { items, ts: Date.now() });
        if (alive) setState({ items, loading: false, error: null });
      })
      .catch(err => {
        console.error('Error loading destination ideas:', err);
        if (alive) setState({ items: [], loading: false, error: err.message || 'feed' });
      });
    return () => { alive = false; };
  // `entry` is identified by its key; a new object for the same trip and
  // centre is the same request.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, today, attempt]);

  const retry = useCallback(() => {
    if (key) cache.delete(key);
    setAttempt(n => n + 1);
  }, [key]);

  return { ...state, retry };
}
